import {
  type AdvancedFilterButtonHandleClickArgs, type BaseOptionsType,
  filterActions, FilterContainer,
} from '@repo/advanced-filters';
import { message } from '@repo/ui';
import { Drawer } from 'antd';
import { useClientReadableCriteria } from 'crm-atoms/hooks';
import { saveCriteria } from 'crm-atoms/utils';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

export default function AdvancedFilterCriteriaBuilder({
  existingCriteriaId,
  open,
  onClose,
  onSave,
  title = 'Select Criteria',
  additionalConditionKeys,
  excludedDefaultKeys = [],
  saveButtonText = 'Add Criteria',
  allowEmpty = true,
  savedTriggerConditionKeys = [],
}: {
  existingCriteriaId?: number;
  open: boolean;
  onClose: () => void;
  onSave: (criteriaId: number | undefined) => void;
  title?: string;
  additionalConditionKeys?: BaseOptionsType[];
  excludedDefaultKeys?: string[],
  saveButtonText?: string,
  allowEmpty?: boolean,
  savedTriggerConditionKeys?: any[],
}) {
  const dispatch = useDispatch<any>();
  const { handleApplyFilter } = useClientReadableCriteria(existingCriteriaId, true);
  const [saving, setSaving] = useState<boolean>(false);

  const handleSave = useCallback(async (json: AdvancedFilterButtonHandleClickArgs) => {
    setSaving(true);
    try {
      if (json.conditions.length) {
        const updatedId = await dispatch(saveCriteria(json));
        onSave(updatedId);
        onClose();
      } else if (allowEmpty) {
        onSave(undefined);
        onClose();
      } else {
        throw new Error('No criteria selected');
      }
    } catch (e: any) {
      message.error(e.message ?? 'Something Went wrong updating the filter');
    } finally {
      setSaving(false);
    }
  }, [allowEmpty, dispatch, onClose, onSave]);

  useEffect(() => {
    if (open && existingCriteriaId) {
      handleApplyFilter();
    } else {
      dispatch(filterActions.clearFilter());
    }
  }, [dispatch, existingCriteriaId, handleApplyFilter, open]);

  return (
    <Drawer
      title={title}
      open={open}
      onClose={onClose}
      placement="bottom"
      height="80vh"
    >
      <FilterContainer
        additionalConditionKeys={additionalConditionKeys?.length ? additionalConditionKeys : savedTriggerConditionKeys}
        excludedDefaultKeys={excludedDefaultKeys}
        footerConfig={[
          {
            children: 'Cancel',
            handleClick: onClose,
          },
          {
            type: 'primary',
            children: saveButtonText,
            handleClick: handleSave,
            disabled: saving,
            loading: saving,
          },
        ]}
      />
    </Drawer>
  );
}
